'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { useSupabaseAuth } from '@/lib/auth/simplified/SupabaseAuthContext';
import { FeatureFlag, useFeatureFlag } from '@/lib/config/features';
import { TranslationSafeWrapper } from './TranslationSafeWrapper';
import { tracking } from '@/lib/tracking';
import { PhoneInput } from './PhoneInput';
import { Turnstile } from '@marsidev/react-turnstile';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { logPhoneFormat, normalizeToE164, isValidE164 } from '@/lib/utils/phone-validation';
import { getSafeRedirectPath, withReloadParam } from '@/lib/utils/safe-redirect';

type LoginStep = 'input' | 'phone-input' | 'sent' | 'verifying' | 'otp' | 'add-phone';
type LoginMethod = 'email' | 'phone';

interface LoginFormProps {
  initialSignUpMode?: boolean;
  disableAutoRedirect?: boolean;  // For room join flow - stay on page
  onLoginSuccess?: () => void;    // Callback instead of redirect
}

/**
 * Migrated LoginForm component that uses the compatibility auth hook
 * This works with either the legacy or improved auth system
 */
export default function LoginFormCompat({
  initialSignUpMode = false,
  disableAutoRedirect = false,
  onLoginSuccess
}: LoginFormProps) {
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning'; text: string } | null>(null);
  const [loginStep, setLoginStep] = useState<LoginStep>('input');
  const [countdown, setCountdown] = useState(0);
  const [isSignUp, setIsSignUp] = useState(initialSignUpMode);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [usePassword, setUsePassword] = useState(false);
  const [needsPhoneCollection, setNeedsPhoneCollection] = useState(false);
  const [maskedPhone, setMaskedPhone] = useState<string | null>(null);

  // Cloudflare Turnstile captcha token. Reset after each submission (Supabase
  // rejects a reused token). Required for both phone and email signup paths
  // unless NEXT_PUBLIC_TURNSTILE_SITE_KEY is unset (dev convenience).
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaSiteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;
  const captchaRequired = !!captchaSiteKey;
  const [loginMethod, setLoginMethod] = useState<LoginMethod>('email');

  // Use Supabase auth context directly
  const {
    signInWithEmail,
    signInWithPhone,
    signInWithOAuth,
    isLoading,
    isAuthenticated,
    profile,
    user: authUser,
  } = useSupabaseAuth();

  // Stable primitive for effect deps: authUser's object identity changes on
  // every auth event (init, sign-in, token refresh), and using the object
  // itself as a dependency would re-run the auto-redirect effect — and its
  // validateServerAuth loop-breaker bookkeeping — several times per visit.
  const isAnonSession = !!authUser?.is_anonymous;

  // `next` param support: invite links arrive as
  // /login?signup=true&invite=<token>&next=/invite/<token>. Only same-origin
  // paths are honored (see getSafeRedirectPath for the open-redirect
  // vectors a naive prefix check misses).
  const getSafeNextParam = () =>
    getSafeRedirectPath(new URLSearchParams(window.location.search).get('next'));
  
  // Use local sending state for the button instead of global auth loading state
  const loading = isSendingOtp;
  
  // Simplified OTP verification function that uses Supabase's callback route
  const verifyOtp = async (phoneOrEmail: string, otp: string, type: 'sms' | 'email') => {
    try {
      console.log(`[LoginFormCompat] Verifying OTP for ${type}:`, phoneOrEmail);

      // Use the singleton Supabase client from our browser utility
      const { createBrowserClient } = await import('@/lib/supabase-clients');
      const supabase = createBrowserClient();

      console.log('[LoginFormCompat] Attempting to verify OTP with Supabase...');

      // CRITICAL: Supabase JavaScript SDK quirk
      // - signInWithOtp requires: phone WITH + prefix (e.g., '+18676896780')
      // - verifyOtp requires: phone WITHOUT + prefix (e.g., '18676896780')
      // This is documented inconsistency in Supabase JS SDK
      let phoneForVerification = phoneOrEmail;
      if (type === 'sms' && phoneOrEmail.startsWith('+')) {
        phoneForVerification = phoneOrEmail.substring(1); // Remove + prefix
        console.log('[LoginFormCompat] Removing + prefix for verification:', {
          original: phoneOrEmail,
          forVerification: phoneForVerification
        });
      }

      // Use the verifyOTP method with appropriate type
      const verifyParams = type === 'sms'
        ? { phone: phoneForVerification, token: otp, type: 'sms' as const }
        : { email: phoneOrEmail, token: otp, type: 'email' as const };

      console.log('[LoginFormCompat] Constructed verifyParams:', JSON.stringify(verifyParams, null, 2));

      const { data, error } = await supabase.auth.verifyOtp(verifyParams);

      if (error) {
        console.error('[LoginFormCompat] OTP verification error:', error);
        return {
          success: false,
          error: error.message || 'Verification failed'
        };
      }

      console.log('[LoginFormCompat] OTP verification successful:', data);

      // No more isNewUser heuristic. The `handle_new_auth_user` trigger
      // (migration 20260602190000) creates the public.users row atomically
      // with the auth.users insert — so by the time we return here, every
      // user has a working profile and routes to /dashboard.
      return {
        success: true,
        error: null
      };
    } catch (error: any) {
      console.error('[LoginFormCompat] Exception in verifyOtp', error);
      return {
        success: false,
        error: error.message || 'Verification failed'
      };
    }
  };
  
  // Check for development mode to show which auth system is being used
  const showDebugInfo = process.env.NODE_ENV === 'development';

  // Initialize from initialSignUpMode prop
  useEffect(() => {
    // Force the state to match the prop, whether true or false
    console.log('initialSignUpMode prop is', initialSignUpMode, 'updating state');
    setIsSignUp(initialSignUpMode);
    
    // We also need to listen for URL changes
    const handleUrlChange = () => {
      const params = new URLSearchParams(window.location.search);
      const signupParam = params.get('signup');
      
      if (signupParam === 'true') {
        console.log('URL changed: signup=true detected, switching to signup mode');
        setIsSignUp(true);
      } else if (signupParam === 'false') {
        console.log('URL changed: signup=false detected, switching to signin mode');
        setIsSignUp(false);
      }
    };
    
    // Add event listener for URL changes
    window.addEventListener('popstate', handleUrlChange);
    
    // Clean up the event listener
    return () => {
      window.removeEventListener('popstate', handleUrlChange);
    };
  }, [initialSignUpMode]);

  // Check URL parameters for error and room context
  const [roomContext, setRoomContext] = useState<string | null>(null);
  
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authError = params.get('error');
    const errorMessage = params.get('message');
    const roomName = params.get('room');
    
    if (authError) {
      setMessage({ 
        type: 'error', 
        text: errorMessage 
          ? decodeURIComponent(errorMessage) 
          : 'Authentication failed. Please try again.' 
      });
      setLoginStep('input');
    }
    
    // Store room context if present
    if (roomName) {
      setRoomContext(roomName);
    }
  }, []);

  // Improved auto-redirect with server validation and stateful token consistency
  useEffect(() => {
    // CRITICAL: Skip auto-redirect when used in room join flow
    if (disableAutoRedirect) {
      console.log('[LoginForm] Auto-redirect disabled (room join mode)');
      return;
    }

    // CRITICAL: An anonymous session is never a completed login. Pattern A
    // signup mints one mid-flow (signInAnonymously → updateUser({ email }));
    // redirecting on it aborts the in-flight updateUser and strands the
    // signup as an orphaned anonymous user with no profile. Room guests who
    // land on /login should also stay here so they can sign in properly.
    if (isAnonSession) {
      console.log('[LoginForm] Anonymous session detected, skipping auto-redirect');
      return;
    }

    // Only run this effect when we're in the initial input step, not during OTP verification
    if (loginStep !== 'input') return;

    // When auth is initializing, don't attempt redirects
    if (loading) return;
    
    // Define a function to safely clear auth state
    const clearAuthState = () => {
      try {
        console.log('[LoginForm] Clearing inconsistent auth state');
        // Get the project ref from environment or fall back to the hardcoded value
        const projectRef = process.env.NEXT_PUBLIC_SUPABASE_URL?.match(/https:\/\/([^.]+)\.supabase\.co/)?.[1] || 'lpjvndsvkvqgkdkvacnn';
        
        // Clear localStorage auth token
        localStorage.removeItem(`sb-${projectRef}-auth-token`);
        
        // Clear any auth-related cookies with proper settings
        const cookieDomain = window.location.hostname.includes('apprendo.co') ? '.apprendo.co' : undefined;
        const cookieSettings = cookieDomain ? `; domain=${cookieDomain}; path=/; secure; samesite=none` : '; path=/; secure';
        
        document.cookie = `sb-${projectRef}-auth-token=; expires=Thu, 01 Jan 1970 00:00:00 UTC${cookieSettings}`;
        document.cookie = `sb-${projectRef}-auth-token-code-verifier=; expires=Thu, 01 Jan 1970 00:00:00 UTC${cookieSettings}`;
        
        // Clear other tracking flags
        localStorage.removeItem('auth_loop_detected');
        localStorage.removeItem('auth_signout_count');
        localStorage.removeItem('auth_processing_timestamp');
        localStorage.removeItem('dashboard_redirect_count');
        localStorage.removeItem('last_login_redirect_time');
        
        return true;
      } catch (e) {
        console.error('[LoginForm] Error clearing auth state:', e);
        return false;
      }
    };
    
    // Skip redirect on very first load to let auth system stabilize
    const isFirstLoad = !localStorage.getItem('auth_initialized');
    
    if (isFirstLoad) {
      console.log('[LoginForm] First page load detected, initializing auth system');
      localStorage.setItem('auth_initialized', 'true');
      
      // Clean up any orphaned verifiers on first load
      try {
        const projectRef = process.env.NEXT_PUBLIC_SUPABASE_URL?.match(/https:\/\/([^.]+)\.supabase\.co/)?.[1] || 'lpjvndsvkvqgkdkvacnn';
        const verifierKey = `sb-${projectRef}-auth-token-code-verifier`;
        const tokenKey = `sb-${projectRef}-auth-token`;
        
        if (!localStorage.getItem(tokenKey) && localStorage.getItem(verifierKey)) {
          console.log('[LoginForm] Cleaning orphaned verifier');
          localStorage.removeItem(verifierKey);
        }
      } catch (e) {
        console.error('[LoginForm] Error cleaning verifiers:', e);
      }
      
      // Early auth check after a delay
      setTimeout(() => {
        console.log('[LoginForm] Checking auth state after initialization');
        
        if (isAuthenticated) {
          // Validate with server before redirecting
          validateServerAuth();
        }
      }, 1500);
      
      return; // Exit early on first load
    }
    
    // Track redirect attempts
    const redirectCount = parseInt(localStorage.getItem('dashboard_redirect_count') || '0');
    const lastRedirectTime = parseInt(localStorage.getItem('last_login_redirect_time') || '0');
    const now = Date.now();
    const redirectedRecently = (now - lastRedirectTime) < 3000; // 3 seconds
    
    // Function to validate auth with the server
    const validateServerAuth = async () => {
      // Fire-time anonymity re-check against the LIVE session: this can be
      // invoked from timeouts whose closures predate Pattern A minting its
      // anonymous session, and redirecting then would abort the in-flight
      // updateUser({ email }).
      try {
        const { createBrowserClient } = await import('@/lib/supabase-clients');
        const { data: { session: liveSession } } = await createBrowserClient().auth.getSession();
        if (liveSession?.user?.is_anonymous) {
          console.log('[LoginForm] Live session is anonymous, skipping server-validated redirect');
          return;
        }
      } catch (e) {
        console.error('[LoginForm] Error reading live session, skipping redirect:', e);
        return;
      }

      // Record this attempt
      localStorage.setItem('dashboard_redirect_count', (redirectCount + 1).toString());
      localStorage.setItem('last_login_redirect_time', now.toString());
      
      console.log('[LoginForm] Validating server auth state');
      
      try {
        // First, check if we have error params in the URL that would prevent redirect
        const params = new URLSearchParams(window.location.search);
        if (params.has('error') || params.has('auth_error')) {
          console.log('[LoginForm] Error in URL params, not redirecting:', params.get('error') || params.get('auth_error'));
          return;
        }
        
        // Check server auth state to validate client auth before redirecting
        const response = await fetch('/api/debug-auth');
        
        if (!response.ok) {
          console.error('[LoginForm] Server auth check failed:', response.status);
          // Don't clear auth on server errors - the server itself might be having issues
          return;
        }
        
        const data = await response.json();
        
        // Determine if we have a valid server-confirmed session
        const hasValidSession = data?.auth?.hasValidSession === true;
        
        if (hasValidSession) {
          console.log('[LoginForm] Server confirmed valid session');

          // Check for profile before redirecting
          const hasProfile = data?.profile?.exists === true;

          // Redirect to appropriate destination. Honor a safe ?next= (e.g.
          // an invite return trip) so every redirect source on /login agrees
          // on the destination instead of racing to different ones.
          const destination = hasProfile
            ? getSafeNextParam() ?? '/dashboard'
            : '/onboarding';
          console.log(`[LoginForm] Redirecting to ${destination} with server-validated session`);
          
          // Force a clean reload to ensure clean state on destination — with
          // the separator chosen by the destination (a ?next= carrying its
          // own query, e.g. /settings/channels?connect=phone, keeps it).
          window.location.replace(withReloadParam(destination, Date.now()));
        } else {
          console.log('[LoginForm] Server reports no valid session despite client auth, clearing state');
          
          // Server says no valid session but client thinks we're authenticated - clear client state
          if (clearAuthState()) {
            // Reload page to reset auth system
            window.location.reload();
          }
        }
      } catch (error) {
        console.error('[LoginForm] Error validating server auth:', error);
      }
    };
    
    // Main redirect logic with server validation
    if (isAuthenticated) {
      console.log('[LoginForm] Client reports authenticated, validating with server');
      
      // Check for potential redirect loops
      if (redirectCount >= 3 && redirectedRecently) {
        console.log('[LoginForm] Potential redirect loop detected, blocking redirects temporarily');
        
        // Reset counters after a cooling period (30 seconds)
        setTimeout(() => {
          localStorage.setItem('dashboard_redirect_count', '0');
          console.log('[LoginForm] Redirect cooldown complete, allowing redirects again');
        }, 30000);
        
        // If we've hit a high redirect count, clear auth state to break loop
        if (redirectCount >= 5) {
          console.log('[LoginForm] Clearing auth state due to excessive redirects');
          clearAuthState();
          
          // Show an error to the user
          setMessage({
            type: 'error',
            text: 'Authentication session was reset due to an issue. Please sign in again.'
          });
          
          // Reload the page after a delay to reset the auth system
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        }
        
        return;
      }
      
      // If we're authenticated and not in a potential loop, validate with server
      validateServerAuth();
    }
  }, [isAuthenticated, loginStep, loading, isAnonSession]);

  // Handle countdown for resend timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!email) {
      setMessage({ type: 'error', text: 'Please enter your email or phone number' });
      return;
    }

    // FEATURE #6: Smart detection - email or phone?
    const input = email.trim();
    const isEmail = input.includes('@');
    const isPhone = /^\+?\d{10,15}$/.test(input.replace(/[\s\-\(\)]/g, ''));

    console.log('[LoginFormCompat] Smart login detection:', { input, isEmail, isPhone });

    try {
      setIsSendingOtp(true);

      if (isPhone) {
        // DIRECT PHONE LOGIN
        console.log('[LoginFormCompat][DirectPhone] Phone number entered, sending SMS OTP');

        const normalized = normalizeToE164(input);
        if (!normalized || !isValidE164(normalized)) {
          setMessage({ type: 'error', text: 'Please enter a valid phone number' });
          return;
        }

        setLoginMethod('phone');
        setPhone(normalized);

        const result = await signInWithPhone(normalized, undefined, captchaToken ?? undefined);
        // Supabase invalidates captcha tokens after a single use; reset.
        setCaptchaToken(null);
        console.log('[LoginFormCompat] signInWithPhone result:', result);

        if (!result.success) {
          setMessage({ type: 'error', text: result.error || 'Failed to send SMS' });
          return;
        }

        setMessage({
          type: 'success',
          text: result.fallbackUsed
            ? 'SMS unavailable. Code sent to your email!'
            : 'Verification code sent to your phone!'
        });
        setLoginStep('otp');
        setCountdown(60);
        return;
      }

      // ─── PATTERN A FORK (v2 — native Supabase Auth flow) ─────────────────
      // The presence of a Turnstile token IS the gate. With one, we take the
      // verify-later path:
      //
      //   1. Check email existence (no captcha consumption — just a read)
      //   2. If exists+verified → fall through to OTP for normal sign-in
      //   3. If exists+unverified → tell user "already sent verification"
      //   4. If new email:
      //      a) supabase.auth.signInAnonymously({ captchaToken, data: {created_via} })
      //         → mints session as anon user (auth.users.is_anonymous=true,
      //         email=NULL, email_confirmed_at=NULL). raw_user_meta_data
      //         carries created_via for the trigger to pick up.
      //      b) supabase.auth.updateUser({ email })
      //         → Supabase appends email to auth.users, transitions
      //         email NULL → set, fires Phase 2 UPDATE trigger which calls
      //         ensure_user_profile(). Supabase ALSO sends its standard email
      //         OTP / verification email natively. email_confirmed_at stays
      //         NULL until the user enters the OTP (in the dashboard banner)
      //         or clicks the link.
      //   5. Redirect to /dashboard — banner takes over from there.
      //
      // The previous version used admin.createUser + verifyOtp(magiclink) to
      // mint the session. That worked for "let them in" but verifyOtp-of-a-
      // magiclink AUTO-CONFIRMS the email as a side effect, breaking the
      // verify-later contract. The native anon-then-email flow avoids that
      // trap entirely — Supabase only confirms when the user actively
      // verifies via OTP code or link click.
      //
      // Wrapped so any failure falls back cleanly to the legacy OTP flow.
      if (captchaToken) {
        setLoginMethod('email');
        try {
          const existsRes = await fetch('/api/auth/email-exists', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
          });

          if (existsRes.ok) {
            const existsData = (await existsRes.json()) as {
              exists: boolean;
              unverified?: boolean;
              isAnonymous?: boolean;
            };

            // Returning unverified user: don't issue a new account; nudge them
            // to check their inbox. The VerifyEmailBanner offers Resend.
            if (existsData.exists && existsData.unverified) {
              setMessage({
                type: 'success',
                text:
                  `We already sent a verification email to ${email}. ` +
                  `Check your inbox (and spam) — sign in there to finish setup.`,
              });
              setCaptchaToken(null);
              return;
            }

            // New email → native Pattern A.
            if (!existsData.exists) {
              const { createBrowserClient } = await import(
                '@/lib/supabase-clients'
              );
              const sb = createBrowserClient();

              // (a) Anonymous sign-in (consumes the captcha token via
              //     Supabase's Bot Protection check). user_metadata.created_via
              //     gets stamped — the Phase 1 trigger reads it back when
              //     the email is added next.
              const { error: anonError } = await sb.auth.signInAnonymously({
                options: {
                  captchaToken,
                  data: { created_via: 'email_signup' },
                },
              });
              setCaptchaToken(null);

              if (anonError) {
                console.error(
                  '[LoginFormCompat][PatternA] signInAnonymously failed:',
                  anonError,
                );
                // Fall through to legacy OTP. User will see the message there.
                throw anonError;
              }

              // (b) Attach the email. Supabase sends its verification email
              //     here (Supabase's "Confirm email change" template — we
              //     customize it to include the OTP code so the banner can
              //     verify in-place). Phase 2 UPDATE trigger fires on the
              //     email transition and creates public.users + domain
              //     atomically.
              const { error: updateError } = await sb.auth.updateUser({
                email,
              });

              if (updateError) {
                console.error(
                  '[LoginFormCompat][PatternA] updateUser({email}) failed:',
                  updateError,
                );
                throw updateError;
              }

              setMessage({
                type: 'success',
                text: `Account created. Check ${email} for a verification code.`,
              });
              // Invite flow arrives with ?next=/invite/<token>; send the new
              // account back there so they can accept (profile now exists via
              // the email_change trigger). Everyone else goes to /dashboard
              // where the VerifyEmailBanner takes over.
              const patternANext = getSafeNextParam();
              const patternADest = patternANext ?? '/dashboard';
              const patternASep = patternADest.includes('?') ? '&' : '?';
              setTimeout(() => {
                window.location.href = `${patternADest}${patternASep}login_success=true&ts=${Date.now()}`;
              }, 300);
              return;
            }
          }

          // exists && verified → fall through to existing OTP flow below
          // (returning sign-in via magic link). Any error response also falls
          // through.
        } catch (patternAError) {
          console.error(
            '[LoginFormCompat][PatternA] Unexpected error, falling back to OTP:',
            patternAError,
          );
        }
      }
      // ─── END PATTERN A FORK ──────────────────────────────────────────────

      // EMAIL LOGIN (existing OTP flow — for returning users and the
      // graceful fallback when Pattern A is off or errors)
      console.log('[LoginFormCompat] Calling signInWithEmail...');
      setLoginMethod('email');
      const result = await signInWithEmail(email, captchaToken ?? undefined);
      // Supabase invalidates captcha tokens after a single use; reset.
      setCaptchaToken(null);
      console.log('[LoginFormCompat] signInWithEmail result:', result);

      if (!result.success) {
        console.error('[LoginFormCompat] Failed:', result.error);
        setMessage({
          type: 'error',
          text: result.error || 'Failed to process request. Please try again.'
        });
        return;
      }

      // APPROACH 1: Email OTP sent, show verification step
      console.log('[LoginFormCompat][Approach1] Email OTP sent, showing verification step');
      setMessage({
        type: 'success',
        text: 'Verification code sent to your email!'
      });
      setLoginStep('otp');
      setCountdown(60);
    } catch (error) {
      console.error('[LoginFormCompat] Exception during email submit:', error);
      setMessage({
        type: 'error',
        text: 'An unexpected error occurred. Please try again.'
      });
    } finally {
      setIsSendingOtp(false);
    }
  };

  // APPROACH 1: handlePhoneSubmit removed (no longer needed)
  // Users don't enter phones during signup anymore

  const handleVerifyOtp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!otpCode) {
      setMessage({ type: 'error', text: 'Please enter the verification code' });
      return;
    }

    try {
      setMessage({ 
        type: 'success', 
        text: 'Verifying code...' 
      });
      
      setLoginStep('verifying'); // Set to verifying immediately to prevent further form submissions

      // APPROACH 1: Verify based on login method
      if (loginMethod === 'phone' && phone) {
        // Phone-based verification
        console.log('[LoginFormCompat][DirectPhone] Verifying phone OTP');
        const result = await verifyOtp(phone, otpCode, 'sms');

        if (!result.success) {
          setMessage({
            type: 'error',
            text: typeof result.error === 'string' ? result.error :
                  (result.error as any)?.message || 'Invalid verification code'
          });
          setLoginStep('otp');
          return;
        }

        // Continue with success flow below
        setMessage({ type: 'success', text: 'Verification successful! Redirecting...' });
      } else {
        // Email-based verification
        if (!email) {
          setMessage({ type: 'error', text: 'Missing email for verification' });
          setLoginStep('otp');
          return;
        }

        console.log('[LoginFormCompat][Approach1] Verifying email OTP for:', email);

        const result = await verifyOtp(email, otpCode, 'email');

        if (!result.success) {
          setMessage({
            type: 'error',
            text: typeof result.error === 'string' ? result.error :
                  (result.error as any)?.message || 'Invalid verification code'
          });
          setLoginStep('otp');
          return;
        }
      }

      // Success handling — both verification branches above return early on
      // failure, so reaching this point means verification succeeded.
      console.log('[LoginForm] OTP verification successful');

      // Clear all auth tracking flags to ensure clean state
      localStorage.removeItem('auth_signout_count');
      localStorage.removeItem('auth_loop_detected');
      localStorage.removeItem('auth_processing_timestamp');
      localStorage.removeItem('dashboard_redirect_count');
      localStorage.removeItem('last_login_redirect_time');

      // Mark successful login
      localStorage.setItem('auth_login_success', 'true');
      localStorage.setItem('auth_login_time', Date.now().toString());

      setMessage({
        type: 'success',
        text: 'Verification successful! Redirecting...',
      });

      // Routing: room-invite link if present, then ?next= (domain-invite links
      // arrive as /login?invite=<token>&next=/invite/<token> — returning there
      // lets the user hit Accept), else /dashboard. We used to branch on a
      // broken `isNewUser` heuristic to send first-timers to /onboarding, but
      // the auth.users INSERT trigger now creates the public.users row
      // atomically — every signed-in user has a working profile.
      const params = new URLSearchParams(window.location.search);
      const redirectUrl = params.get('redirect');
      const safeNext = getSafeNextParam();
      const destination =
        redirectUrl && redirectUrl.startsWith('/rooms/')
          ? redirectUrl
          : safeNext ?? '/dashboard';

      const sep = destination.includes('?') ? '&' : '?';
      const url = `${destination}${sep}login_success=true&ts=${Date.now()}`;
      console.log(`[LoginForm] Final redirect to ${destination}`);

      setTimeout(() => {
        if (disableAutoRedirect && onLoginSuccess) {
          console.log('[LoginForm] Login successful, calling onLoginSuccess callback');
          onLoginSuccess();
        } else {
          window.location.href = url;
        }
      }, 500);
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: 'An unexpected error occurred. Please try again.'
      });
      console.error('[LoginForm] Unexpected OTP verification error:', error);
      setLoginStep('otp'); // Return to OTP input to let user try again
    }
  };

  const resetForm = () => {
    setLoginStep('input');
    setMessage(null);
    setOtpCode('');
  };

  const handlePasswordAuth = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      setMessage({ type: 'error', text: 'Please enter both email and password' });
      return;
    }

    try {
      setIsSendingOtp(true);
      setMessage(null);

      const { createBrowserClient } = await import('@/lib/supabase-clients');
      const supabase = createBrowserClient();

      if (isSignUp) {
        // Sign up with password
        console.log('[LoginFormCompat] Signing up with password');
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/api/auth/callback`
          }
        });

        if (error) {
          console.error('[LoginFormCompat] Password sign-up error:', error);
          setMessage({
            type: 'error',
            text: error.message || 'Failed to create account. Please try again.'
          });
        } else if (data.user) {
          setMessage({
            type: 'success',
            text: 'Account created! Please check your email to confirm your account.'
          });
          // Track signup
          tracking.trackSignUp('password');
        }
      } else {
        // Sign in with password
        console.log('[LoginFormCompat] Signing in with password');
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password
        });

        if (error) {
          console.error('[LoginFormCompat] Password sign-in error:', error);
          setMessage({
            type: 'error',
            text: error.message || 'Invalid email or password. Please try again.'
          });
        } else if (data.session) {
          setMessage({
            type: 'success',
            text: 'Signed in successfully! Redirecting...'
          });

          // Track login
          tracking.trackLogin('password');

          // Redirect based on user profile. The redirect param is
          // user-supplied — validate it to same-origin paths (raw use here
          // was an open redirect).
          const params = new URLSearchParams(window.location.search);
          const destination =
            getSafeRedirectPath(params.get('redirect')) || '/dashboard/posts';

          setTimeout(() => {
            window.location.href = destination;
          }, 500);
        }
      }
    } catch (error: any) {
      console.error('[LoginFormCompat] Exception during password auth:', error);
      setMessage({
        type: 'error',
        text: 'An unexpected error occurred. Please try again.'
      });
    } finally {
      setIsSendingOtp(false);
    }
  };

  const resendCode = async () => {
    if (countdown > 0) return;

    try {
      if (loginMethod === 'phone' && phone) {
        // Resend SMS OTP
        console.log('[LoginFormCompat][DirectPhone] Resending SMS OTP');
        const result = await signInWithPhone(phone);

        if (result.success) {
          setMessage({
            type: 'success',
            text: 'New verification code sent to your phone!'
          });
          setCountdown(60);
        } else {
          setMessage({
            type: 'error',
            text: result.error || 'Failed to send new code'
          });
        }
      } else {
        // Resend EMAIL OTP
        console.log('[LoginFormCompat][Approach1] Resending email OTP');
        const result = await signInWithEmail(email);

        if (result.success) {
          setMessage({
            type: 'success',
            text: 'New verification code sent to your email!'
          });
          setCountdown(60);
        } else {
          setMessage({
            type: 'error',
            text: result.error || 'Failed to send new code'
          });
        }
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'An unexpected error occurred. Please try again.'
      });
      console.error('Resend error:', error);
    }
  };

  // APPROACH 1: Phone input removed from signup flow
  // Users add phone AFTER login via Settings or Admin
  // This step is no longer needed

  // Render different UI based on login step
  if (loginStep === 'otp') {
    return (
      <TranslationSafeWrapper>
        <div className="max-w-md w-full mx-auto p-6 bg-card rounded-lg shadow-md notranslate" translate="no">
        {/* Room invitation context */}
        {roomContext && (
          <div className="mb-6 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
            <div className="flex items-center gap-2 text-purple-700 dark:text-purple-300">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <span className="text-sm font-medium">
                Almost there! Verify to join <strong>{roomContext.split(/(?=[A-Z])|[\s-_]/)[0]}'s conversation</strong>
              </span>
            </div>
          </div>
        )}
        
        <h2 className="text-2xl font-bold mb-6 text-center text-foreground">Enter Verification Code</h2>
        
        {/* Auth system debug info */}
        {showDebugInfo && (
          <div className="text-xs mb-4 px-2 py-1 rounded text-center bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100">
            Using simplified auth system
          </div>
        )}

        <div className="space-y-4">
          <div className="bg-blue-50 dark:bg-blue-900 p-4 rounded-md text-blue-800 dark:text-blue-100">
            {loginMethod === 'phone' ? (
              <>
                <p className="mb-2">We've sent a verification code to <strong>{phone}</strong></p>
                <p className="mt-2">Check your messages and enter the 6-digit code to sign in.</p>
              </>
            ) : (
              <>
                <p className="mb-2">We've sent a verification code to <strong>{email}</strong></p>
                <p className="mt-2">Check your email and enter the 6-digit code to sign in.</p>
              </>
            )}
          </div>
          
          {message && (
            <div className={`p-3 rounded-md ${
              message.type === 'success'
                ? 'bg-green-50 dark:bg-green-900 text-green-800 dark:text-green-100'
                : message.type === 'warning'
                ? 'bg-yellow-50 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-100'
                : 'bg-red-50 dark:bg-red-900 text-red-800 dark:text-red-100'
            }`}>
              {message.text}
            </div>
          )}
          
          <form onSubmit={handleVerifyOtp} className="space-y-4 notranslate" translate="no">
            <div className="form-group">
              <label htmlFor="otp" className="block text-sm font-medium text-foreground mb-1">
                Verification code
              </label>
              <input
                id="otp"
                type="text"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value)}
                placeholder="Enter code from email"
                className="w-full px-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring bg-background text-foreground notranslate"
                required
                autoComplete="one-time-code"
                maxLength={6}
                translate="no"
                spellCheck="false"
                data-preserve="true"
              />
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-primary-foreground py-2 px-4 rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Verifying...' : 'Verify Code'}
            </button>
          </form>
          
          <div className="flex flex-col space-y-3 pt-2">
            <button
              onClick={resendCode}
              disabled={countdown > 0 || loading}
              className="w-full bg-muted text-muted-foreground py-2 px-4 rounded-md hover:bg-muted/80 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Sending...' : countdown > 0 ? `Resend in ${countdown}s` : 'Resend verification code'}
            </button>
            
            <button
              onClick={resetForm}
              className="text-primary hover:underline text-sm font-medium"
            >
              Use a different number
            </button>
          </div>
        </div>

        <div className="mt-6 text-sm text-muted-foreground">
          <p>
            {loginMethod === 'phone'
              ? "Can't find the SMS? Check your messages or try resending the code."
              : "Can't find the email? Check your spam folder or try resending the code."}
          </p>
        </div>
        </div>
      </TranslationSafeWrapper>
    );
  }
  
  if (loginStep === 'verifying') {
    return (
      <TranslationSafeWrapper>
        <div className="max-w-md w-full mx-auto p-6 bg-card rounded-lg shadow-md notranslate" translate="no">
        <h2 className="text-2xl font-bold mb-6 text-center text-foreground">Verifying Your Login</h2>
        
        {/* Auth system debug info */}
        {showDebugInfo && (
          <div className="text-xs mb-4 px-2 py-1 rounded text-center bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100">
            Using simplified auth system
          </div>
        )}
        
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary mb-4"></div>
          <p className="text-foreground">Checking authentication status...</p>
          <p className="mt-2 text-sm text-muted-foreground">This may take a few moments</p>
        </div>
        
        <div className="mt-6 text-center">
          <button
            onClick={resetForm}
            className="text-primary hover:underline text-sm font-medium"
          >
            Cancel and return to login
          </button>
        </div>
        </div>
      </TranslationSafeWrapper>
    );
  }

  // Default input step
  return (
    <TranslationSafeWrapper>
      <div className="max-w-md w-full mx-auto p-6 bg-card rounded-lg shadow-md notranslate" translate="no">
      {/* Room invitation context */}
      {roomContext && (
        <div className="mb-6 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
          <div className="flex items-center gap-2 text-purple-700 dark:text-purple-300">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            <span className="text-sm font-medium">
              You're joining <strong>{roomContext.split(/(?=[A-Z])|[\s-_]/)[0]}'s HD Conversation</strong>
            </span>
          </div>
          <p className="text-xs text-purple-600 dark:text-purple-400 mt-2">
            {isSignUp ? "Create your account to join the conversation" : "Sign in to join the conversation"}
          </p>
        </div>
      )}
      
      <h2 className="text-2xl font-bold mb-6 text-center text-foreground">
        {isSignUp ? "Create Account" : "Sign in to Apprendo"}
      </h2>
      
      {/* Auth system debug info */}
      {showDebugInfo && (
        <div className="text-xs mb-4 px-2 py-1 rounded text-center bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100">
          Using simplified auth system
        </div>
      )}

      {/* Microsoft OAuth button */}
      <div className="space-y-4 mb-2">
        <button
          type="button"
          disabled={loading}
          onClick={() => signInWithOAuth('azure')}
          className="w-full flex items-center justify-center gap-3 border border-input bg-background text-foreground py-2 px-4 rounded-md hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 23 23">
            <rect x="1" y="1" width="10" height="10" fill="#f25022"/>
            <rect x="12" y="1" width="10" height="10" fill="#7fba00"/>
            <rect x="1" y="12" width="10" height="10" fill="#00a4ef"/>
            <rect x="12" y="12" width="10" height="10" fill="#ffb900"/>
          </svg>
          Continue with Microsoft
        </button>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-card px-2 text-muted-foreground">Or continue with email</span>
          </div>
        </div>
      </div>

      <form onSubmit={usePassword ? handlePasswordAuth : handleSubmit} className="space-y-4 notranslate" translate="no">
        {!usePassword && (
          <div className="form-group">
            <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1">
              Email address
            </label>
            <input
              id="email"
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com or +1234567890"
              className="w-full px-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring bg-background text-foreground notranslate"
              required
              autoComplete="email tel"
              autoFocus
              translate="no"
              spellCheck="false"
              data-preserve="true"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Enter your email or phone number
            </p>
          </div>
        )}

        {usePassword && (
          <>
            <div className="form-group">
              <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1">
                Email address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full px-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring bg-background text-foreground notranslate"
                required
                autoComplete="username email"
                translate="no"
                spellCheck="false"
                data-preserve="true"
              />
            </div>
            <div className="form-group">
              <label htmlFor="password" className="block text-sm font-medium text-foreground mb-1">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="w-full px-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring bg-background text-foreground notranslate"
                required
                autoComplete={isSignUp ? 'new-password' : 'current-password'}
                translate="no"
                spellCheck="false"
                data-preserve="true"
                minLength={6}
              />
              {isSignUp && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Minimum 6 characters
                </p>
              )}
            </div>
          </>
        )}

        {message && (
          <div className={`p-3 rounded-md ${
            message.type === 'success'
              ? 'bg-green-50 dark:bg-green-900 text-green-800 dark:text-green-100'
              : message.type === 'warning'
              ? 'bg-yellow-50 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-100'
              : 'bg-red-50 dark:bg-red-900 text-red-800 dark:text-red-100'
          }`}>
            {message.text}
          </div>
        )}

        {/* Turnstile bot protection. Renders only when NEXT_PUBLIC_TURNSTILE_SITE_KEY
            is set so local dev (with the env var unset) keeps working. Token is
            reset after each submission since Supabase rejects reused tokens. */}
        {captchaSiteKey && !usePassword && (
          <div className="flex justify-center">
            <Turnstile
              siteKey={captchaSiteKey}
              onSuccess={setCaptchaToken}
              onExpire={() => setCaptchaToken(null)}
              onError={() => setCaptchaToken(null)}
              options={{ theme: 'auto', size: 'flexible' }}
            />
          </div>
        )}

        <button
          type="submit"
          disabled={loading || (captchaRequired && !usePassword && !captchaToken)}
          className="w-full bg-primary text-primary-foreground py-2 px-4 rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (usePassword ? 'Processing...' : 'Sending...') : isSignUp ? 'Create Account' : 'Sign In'}
        </button>

        {/* Toggle between password and OTP */}
        <div className="text-center">
          <button
            type="button"
            onClick={() => {
              setUsePassword(!usePassword);
              setMessage(null);
              setPassword('');
            }}
            className="text-sm text-primary hover:underline"
          >
            {usePassword ? 'Use SMS verification instead' : 'Sign in with password'}
          </button>
        </div>
      </form>

      <div className="mt-4 text-center">
        <p className="text-sm text-muted-foreground">
          {isSignUp ? "Already have an account?" : "Don't have an account?"}
          <a
            href={isSignUp ? "/login?signup=false" : "/login?signup=true"}
            className="ml-1 text-primary hover:underline focus:outline-none"
            onClick={(e) => {
              // Prevent default navigation
              e.preventDefault();

              // Toggle state locally
              const newSignUpState = !isSignUp;
              setIsSignUp(newSignUpState);

              // Update URL
              const url = new URL(window.location.href);
              url.searchParams.set('signup', newSignUpState ? 'true' : 'false');

              // Use replace to update URL without navigation
              window.history.replaceState({}, '', url.toString());

              console.log(`Switched to ${newSignUpState ? 'Create Account' : 'Sign In'} mode, URL updated`);
            }}
          >
            {isSignUp ? "Sign In" : "Create Account"}
          </a>
        </p>
      </div>

      <div className="mt-6 border-t border-border pt-4">
        <p className="text-sm text-muted-foreground text-center">
          {isSignUp
            ? "We'll send you a verification code to create your account."
            : "We'll send you a verification code for secure sign in."}
        </p>

        <p className="text-xs text-muted-foreground text-center mt-2">
          Tip: You can login with your email or phone number.
        </p>
        
        <div className="mt-4 text-xs text-muted-foreground text-center">
          <p className="mb-1">Your code is valid for 10 minutes</p>
          <p>Environment: {typeof window !== 'undefined' ? window.location.hostname : 'server'}</p>
        </div>
      </div>
      </div>
    </TranslationSafeWrapper>
  );
}